import { FileData } from "@/utils/types";
import { create } from "zustand";

interface Story {
  name: string;
  description: string;
  variant_props: {
    property_name: string;
    property_value: string;
  };
}

interface GenerationContext {
  selectedFiles: FileData[];
  examples: string[];
  notes: string;
  componentOverview: string;
  props: string[];
  stories: Story[];
  name: string;
}

export enum GenerationStatus {
  Pending,
  ContextSelection,
  GeneratingContext,
  Editing,
  GeneratingStory,
  WaitingForUserAction,
  Error,
  Success,
}

interface GenerationOutput {
  filename: string;
  content: string;
}

interface GenerationState {
  status: GenerationStatus;
  context: GenerationContext;
  output: GenerationOutput;
}

interface GenerationStore {
  generation: GenerationState;
  setStatus: (status: GenerationStatus) => void;
  setSelectedFiles: (files: FileData[]) => void;
  setExamples: (examples: string[]) => void;
  setNotes: (notes: string) => void;
  setComponentOverview: (overview: string) => void;
  setProps: (props: string[]) => void;
  setStories: (stories: Story[]) => void;
  setName: (name: string) => void;
  resetGeneration: () => void;
  createGenerationForNewStory: () => void;
  addFile: (file: FileData) => void;
  addExample: (example: string) => void;
  removeExample: (index: number) => void;
  handleStateTransition: (action: string) => void;
  setOutputFilename: (filename: string) => void;
  setOutputContent: (content: string) => void;
  resetDesignSystemStore: () => void;
}

const useDesignSystemStore = create<GenerationStore>((set, get) => ({
  generation: {
    status: GenerationStatus.Pending,
    context: {
      selectedFiles: [],
      examples: [],
      notes: "",
      componentOverview: "",
      props: [],
      stories: [],
      name: "",
    },
    output: {
      filename: "",
      content: "",
    },
  },
  setOutputFilename: (filename) =>
    set((state) => ({
      generation: {
        ...state.generation,
        output: {
          ...state.generation.output,
          filename,
        },
      },
    })),
  setOutputContent: (content) =>
    set((state) => ({
      generation: {
        ...state.generation,
        output: {
          ...state.generation.output,
          content,
        },
      },
    })),
  setStatus: (status) =>
    set((state) => ({ generation: { ...state.generation, status } })),
  setSelectedFiles: (selectedFiles) => {
    set((state) => ({
      generation: {
        ...state.generation,
        context: { ...state.generation.context, selectedFiles },
      },
    }));
    get().handleStateTransition("setSelectedFiles");
  },
  addFile: (file: FileData) => {
    set((state) => {
      if (state.generation.context.selectedFiles.includes(file)) {
        return state;
      }
      return {
        generation: {
          ...state.generation,
          context: {
            ...state.generation.context,
            selectedFiles: [...state.generation.context.selectedFiles, file],
          },
        },
      };
    });
    get().handleStateTransition("addFile");
  },
  setExamples: (examples) => {
    set((state) => ({
      generation: {
        ...state.generation,
        context: { ...state.generation.context, examples },
      },
    }));
    get().handleStateTransition("setExamples");
  },
  addExample: (example: string) => {
    set((state) => ({
      generation: {
        ...state.generation,
        context: {
          ...state.generation.context,
          examples: [...state.generation.context.examples, example],
        },
      },
    }));
    get().handleStateTransition("addExample");
  },
  removeExample: (index: number) => {
    set((state) => ({
      generation: {
        ...state.generation,
        context: {
          ...state.generation.context,
          examples: state.generation.context.examples.filter(
            (_, i) => i !== index
          ),
        },
      },
    }));
    get().handleStateTransition("removeExample");
  },
  setNotes: (notes) => {
    set((state) => ({
      generation: {
        ...state.generation,
        context: { ...state.generation.context, notes },
      },
    }));
    get().handleStateTransition("setNotes");
  },
  setComponentOverview: (componentOverview) => {
    set((state) => ({
      generation: {
        ...state.generation,
        context: { ...state.generation.context, componentOverview },
      },
    }));
    get().handleStateTransition("setComponentOverview");
  },
  setProps: (props) => {
    set((state) => ({
      generation: {
        ...state.generation,
        context: { ...state.generation.context, props },
      },
    }));
    get().handleStateTransition("setProps");
  },
  setStories: (stories) => {
    set((state) => ({
      generation: {
        ...state.generation,
        context: { ...state.generation.context, stories },
      },
    }));
    get().handleStateTransition("setStories");
  },
  setName: (name) => {
    set((state) => ({
      generation: {
        ...state.generation,
        context: { ...state.generation.context, name },
      },
    }));
    get().handleStateTransition("setName");
  },
  createGenerationForNewStory: () => {
    set((state) => ({
      generation: {
        ...state.generation,
        status: GenerationStatus.ContextSelection,
      },
    }));
    get().handleStateTransition("createGenerationForNewStory");
  },
  resetGeneration: () =>
    set(() => ({
      generation: {
        status: GenerationStatus.Pending,
        context: {
          selectedFiles: [],
          examples: [],
          notes: "",
          componentOverview: "",
          props: [],
          stories: [],
          name: "",
        },
        output: {
          filename: "",
          content: "",
        },
      },
    })),
  handleStateTransition: (action: string) => {
    const { status, context } = get().generation;

    if (status === GenerationStatus.Pending) {
      if (
        context.selectedFiles.length > 0 ||
        context.examples.length > 0 ||
        context.notes.length > 0
      ) {
        set((state) => ({
          generation: {
            ...state.generation,
            status: GenerationStatus.ContextSelection,
          },
        }));
      }
    }
  },
  resetDesignSystemStore: () =>
    set(() => ({
      generation: {
        status: GenerationStatus.Pending,
        context: {
          selectedFiles: [],
          examples: [],
          notes: "",
          componentOverview: "",
          props: [],
          stories: [],
          name: "",
        },
        output: {
          filename: "",
          content: "",
        },
      },
    })),
}));

export default useDesignSystemStore;
